import { useState, useEffect } from 'react'
import { LogIn } from 'lucide-react'

/**
 * Persistent banner shown when the session has expired and silent refresh failed.
 * Non-dismissable — the doctor must sign in again to continue saving.
 *
 * ## Why this does NOT call `logout()` (CLA-1370)
 *
 * It used to, and that made the sentence above the button a lie. `logout()` runs
 * `clearSensitiveStorage()`, which wipes every `claro:draft:*` key — note bodies,
 * the scribe pad, clinical history, personal notes — and deletes the
 * `claro-call-recordings` IndexedDB. So the one control offered to someone whose
 * session expired destroyed the unsaved work the banner had just promised was safe.
 *
 * Worse, the wipe ran synchronously BEFORE the navigation, and `AppShell` holds a
 * `beforeunload` guard while recording. A clinician who clicked Sign in mid-consult,
 * got the browser's "Leave site?" prompt and chose Stay had already lost their
 * drafts while sitting on a page that looked untouched.
 *
 * **An expiry is not a logout.** A logout is someone saying "I am done, and this may
 * be a shared machine" — clearing PHI there is correct and stays as it is (SEC-014).
 * An expiry is the same person, still working, whose token simply aged out. They are
 * about to prove who they are again; their work should survive it.
 *
 * This now does exactly what `auth.ts`'s own 401 path does — navigate to
 * `/login?next=<here>` and touch no local state — so the two ways out of an expired
 * session (click the button, or just reload) finally behave the same. Reloading
 * already preserved drafts; only the button did not.
 *
 * ⚠ Do not "simplify" this back to `logout()`. The destructive call is the bug.
 */
export function AuthBanner() {
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    const handler = () => setExpired(true)
    window.addEventListener('claro:auth-error', handler)
    return () => window.removeEventListener('claro:auth-error', handler)
  }, [])

  if (!expired) return null

  // Same shape as the 401 branch of `useAuthStore.init()` — carry the intended
  // destination so signing in lands back here rather than on "/" (CLA-1032).
  // The banner's old `logout()` path dropped it.
  const signIn = () => {
    const next =
      window.location.pathname + (window.location.search || '') + (window.location.hash || '')
    window.location.href = next === '/' ? '/login' : '/login?next=' + encodeURIComponent(next)
  }

  return (
    <div className="bg-error/10 border-b border-error/20 px-4 py-3 flex items-center justify-between gap-3 shrink-0">
      <p className="text-sm text-on-error-soft font-medium">
        Your session has expired. Any unsaved changes are preserved locally — sign in to continue.
      </p>
      <button
        onClick={signIn}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-error rounded-lg hover:bg-error/90 transition-colors shrink-0"
      >
        <LogIn className="w-4 h-4" />
        Sign in
      </button>
    </div>
  )
}
